const suffixV1 = '/api/v1/'
const suffixV2 = '/api/v2/'
import _ from 'lodash'
import qs from 'qs'
import * as AloAi from 'src/constants/aloai'
import { DEFAULT_PINNED_LIST } from 'src/constants/contacts-list-default-pinned-list'

const exportCommunications = async (contactId) => {
  return window.axios.get(`${suffixV2}contacts/${contactId}/export-communications`)
}

const talk2Api = {
  V1: {
    contact: {
      createAxiosError (message, status) {
        let error = new Error(message)
        error.response = {
          status: status,
          data: {
            error: message
          }
        }
        return error
      },
      get: function (id) {
        if (!id) {
          return null
        }

        return window.axios.get(`${suffixV1}contact/${id}`)
      },

      create (params) {
        return window.axios.post(`${suffixV1}contact`, params)
      },

      getAttributes (id) {
        if (!id) {
          return null
        }

        return window.axios.get(`${suffixV1}contact-attributes/${id}`)
      },

      update: function (id, params) {
        if (!id) {
          return null
        }

        return window.axios.put(`${suffixV1}contact/${id}`, params)
      },

      getPhoneNumbers (id) {
        if (!id) {
          return Promise.reject(new Error('Failed to get phone numbers, missing contact id!'))
        }

        return window.axios.get(`${suffixV1}contact/${id}/phone-numbers`)
      },

      getRingGroups (id) {
        if (!id) {
          return null
        }

        return window.axios.get(`${suffixV1}contact/${id}/ring-groups`)
      },

      getLiveAloAiCall (id, config = {}) {
        return window.axios.get(`${suffixV1}contact/${id}/live-aloai-call`, config)
      },

      endLiveAloAiCall (id) {
        return window.axios.post(`${suffixV1}contact/${id}/live-aloai-call/end`)
      },

      storeTags (id, params) {
        if (!id) {
          return null
        }

        return window.axios.post(`${suffixV1}contact/${id}/tag`, params)
      },

      storeLines (id, params) {
        if (!id) {
          return null
        }

        return window.axios.put(`${suffixV1}contact/${id}/campaigns`, params)
      },

      storeRingGroups (id, params) {
        if (!id) {
          return null
        }

        return window.axios.put(`${suffixV1}contact/${id}/ring-groups`, params)
      },

      storePhone (id, params) {
        if (!id) {
          return null
        }

        return window.axios.post(`${suffixV1}contact/${id}/phone-number`, params)
      },

      updatePhone (id, phoneId, params) {
        if (!id || !phoneId) {
          return null
        }

        return window.axios.post(`${suffixV1}contact/${id}/phone-number/${phoneId}`, params)
      },

      deletePhone (id, phoneId) {
        if (!id || !phoneId) {
          return null
        }

        return window.axios.delete(`${suffixV1}contact/${id}/phone-number/${phoneId}`)
      },

      dispose (id, params) {
        if (!id) {
          return null
        }

        return window.axios.post(`${suffixV1}contact/${id}/dispose`, params)
      },

      sendEmail (id, params) {
        if (!id) {
          return null
        }

        return window.axios.post(`${suffixV1}contact/${id}/send-email`, params)
      },

      addEngagement (id, params) {
        if (!id) {
          return null
        }

        return window.axios.post(`${suffixV1}calendar/events/contact/${id}/create`, params)
      },

      updateEngagement (contactId, eventId, params) {
        return window.axios.post(`${suffixV1}calendar/events/contact/${contactId}/update/${eventId}`, params)
      },

      getLineIncomingNumber (contactId, lineId, params = null) {
        if (!contactId || !lineId) {
          return null
        }

        return window.axios.get(`${suffixV1}contact/${contactId}/campaign/${lineId}/get-incoming-number`, { params })
      },

      getIntegrationData (contactId, params) {
        if (!contactId) {
          return null
        }

        return window.axios.get(`${suffixV1}contact/${contactId}/integration-data`, params)
      },

      unDnc (id, reason) {
        return window.axios.patch(`${suffixV1}contact/${id}/undnc`, {
          reason: reason
        })
      },

      getSequenceInfo (id) {
        return window.axios.get(`${suffixV1}contact/${id}/sequence-info`)
      },

      disenrollFromSequence (id) {
        return window.axios.post(`${suffixV1}contact/${id}/disenroll`)
      },

      syncHubspot (id) {
        return window.axios.post(`${suffixV1}contact/${id}/sync-hubspot`)
      },

      syncSalesforce (id) {
        return window.axios.post(`${suffixV1}contact/${id}/sync-salesforce`)
      },

      forceCreateAlowareContactViaSalesforce (id, type) {
        return window.axios.post(`${suffixV1}integrations/salesforce/force-create-aloware-contact`, {
          objectType: type,
          recordId: id
        })
      },

      syncGuesty (id) {
        return window.axios.post(`${suffixV1}contact/${id}/sync-guesty`)
      },

      syncZoho (id) {
        return window.axios.post(`${suffixV1}contact/${id}/sync-zoho`)
      },

      syncPipedrive (id) {
        return window.axios.post(`${suffixV1}contact/${id}/sync-pipedrive`)
      },

      syncGHL (id) {
        return window.axios.post(`${suffixV1}contact/${id}/sync-gohighlevel`)
      },

      syncClioManage (id) {
        return window.axios.post(`${suffixV1}contact/${id}/sync-clio-manage`)
      },

      selectClioManageLoggingMatter (id, clioMatterId) {
        return window.axios.post(`${suffixV1}contact/${id}/clio-manage/logging-matter`, {
          clio_matter_id: clioMatterId
        }, {
          // No global axios timeout is configured, and the caller disables its
          // controls while this is open — so a hung request would leave them
          // disabled until the component remounts. Generous enough for the Clio
          // round trip behind this endpoint, which carries its own retry budget.
          timeout: 60000
        })
      },

      bulkSaveCustomAttributes (contactId, params) {
        return window.axios.patch(`${suffixV1}contact-attributes/${contactId}`, params)
      },

      getCommunicationsSummary (contactId) {
        if (!contactId) {
          return null
        }

        return window.axios.get(`${suffixV1}contact/${contactId}/communications-summary`)
      }
    },

    suppressList: {
      check (phoneNumber, contactId) {
        if (!phoneNumber) {
          return Promise.reject(new Error('Missing phone number for suppress-list check!'))
        }

        return window.axios.get(`${suffixV1}phone-number/check-suppression`, {
          params: { phone_number: phoneNumber, contact_id: contactId }
        })
      }
    },

    tags: {
      get (params) {
        return window.axios.get(`${suffixV1}tag`, params)
      },

      create (params) {
        return window.axios.post(`${suffixV1}tag`, params)
      },

      update (id, params) {
        return window.axios.patch(`${suffixV1}tag/${id}`, params)
      },

      delete (id, params) {
        return window.axios.delete(`${suffixV1}tag/${id}`, params)
      },

      count (params = {}) {
        return window.axios.get(`${suffixV1}tags/count`, { params })
      },

      bulkDelete (params) {
        return window.axios.delete(`${suffixV1}tags/bulk-delete`, params)
      },

      split (id, params) {
        return window.axios.post(`${suffixV1}tags/${id}/split`, params)
      },

      assignContactsTo (id, params) {
        return window.axios.post(`${suffixV1}tags/${id}/assign-contacts-to`, params)
      },

      bulkAssignContactsTo (params) {
        return window.axios.post(`${suffixV1}tags/bulk-assign-contacts-to`, params)
      },

      convertTagToList (params) {
        return window.axios.post(`${suffixV2}contacts-list/convert`, params)
      },

      addTasksToUserPowerDialer (id, params) {
        return window.axios.post(`${suffixV1}tags/${id}/add-to-user-power-dialer`, params)
      },

      bulkAddTasksToUserPowerDialer (params) {
        return window.axios.post(`${suffixV1}tags/bulk-add-to-user-power-dialer`, params)
      }
    },

    lines: {
      get () {
        return window.axios.get(`${suffixV1}campaign`)
      },

      fileUpload (lineId, params, events) {
        return window.axios.post(`${suffixV1}uploaded-files/${lineId}`, params, events)
      },

      faxUpload (lineId, params, events) {
        return window.axios.post(`${suffixV1}campaign/${lineId}/upload/fax`, params, events)
      },

      sendFax (lineId, contactId, params) {
        return window.axios.post(`${suffixV1}campaign/send-fax/${lineId}/${contactId}`, params)
      },

      rcsDetails (lineId) {
        return window.axios.get(`${suffixV1}campaign/${lineId}/rcs-details`)
      },

      rcsFallbackLine (lineId) {
        return window.axios.get(`${suffixV1}campaign/${lineId}/rcs-fallback-line`)
      }
    },

    filters: {
      async get ({ isOnCompany } = {}) {
        const { data = [] } = await window.axios.get(`${suffixV1}filter`)

        if (isOnCompany) {
          return data.filter(filter => filter.is_on_company)
        }

        return data
      }
    },

    ringGroups: {
      get () {
        return window.axios.get(`${suffixV1}ring-group`)
      },
      updateMissedCallSettings (id, params) {
        return window.axios.patch(`${suffixV1}ring-group/${id}/missed-call-settings`, params)
      },
      uploadFile (id, type, formData) {
        return window.axios.post(`${suffixV1}ring-group/${id}/upload-file/${type}`, formData)
      },
      deleteFile (id, type) {
        return window.axios.delete(`${suffixV1}ring-group/${id}/delete/${type}`)
      }
    },

    message: {
      send (params) {
        return window.axios.post(`${suffixV1}messages`, params)
      },

      scheduled (params) {
        return window.axios.post(`${suffixV1}scheduled-messages`, params)
      },

      getScheduledByContact (contactId, params) {
        return window.axios.get(`${suffixV1}scheduled-messages/${contactId}`, { params })
      },

      deleteScheduledMessage (messageId) {
        return window.axios.delete(`${suffixV1}scheduled-messages/${messageId}`)
      }
    },

    smsTemplate: {
      get () {
        return window.axios.get(`${suffixV1}sms-template`)
      },

      create (params) {
        return window.axios.post(`${suffixV1}sms-template`, params)
      },

      update (id, params) {
        return window.axios.put(`${suffixV1}sms-template/${id}`, params)
      },

      delete (id) {
        return window.axios.delete(`${suffixV1}sms-template/${id}`)
      }
    },

    integrations: {
      salesforce: {
        getList (params) {
          return window.axios.get(`${suffixV1}integration/salesforce/lists`, params)
        }
      },
      hubspot: {
        getWorkflows () {
          return window.axios.get(`${suffixV1}integration/hubspot/workflows`)
        },

        enrollToWorkflow (params) {
          return window.axios.post(`${suffixV1}integration/hubspot/enroll-contact`, params)
        },

        getList (params) {
          return window.axios.get(`${suffixV1}integration/hubspot/lists`, params)
        },

        /**
         * Update a contact's Lifecycle Stage in HubSpot
         *
         * @param {string|number} contactId
         * @param {string} lifecycleStage
         * @param {boolean} resetRequired
         * @returns {axios.AxiosResponse<{success: boolean, message: string}>}
         */
        async updateLifecycleStage (contactId, lifecycleStage, resetRequired = false) {
          let response = null
          try {
            response = await window.axios.patch(`${suffixV1}integrations/hubspot/lifecycle-stage`, {
              contact_id: contactId,
              lifecycle_stage: lifecycleStage,
              reset_required: resetRequired
            })
          } catch (error) {
            response = error.response
          }

          return response
        },

        /**
         * Get the available lifecycle stages
         *
         * @param currentStage the contact's stage, so a stage missing from the cached list is fetched fresh
         * @returns Promise<axios.AxiosResponse<{success: boolean, data: {lifecycle_stages: Object, can_update_lifecycle_stages: boolean}}>>
         */
        async getLifecycleStages (currentStage = null) {
          return window.axios.get(`${suffixV1}integrations/hubspot/jit-card/lifecycle-stages`, {
            params: { current_stage: currentStage || undefined }
          })
        },

        /**
         * Get the company association of the contact
         *
         * @param contactId
         * @returns Promise<axios.AxiosResponse<{success: boolean, data: object>>
         */
        getContactCompanyAssociation (contactId) {
          if (!contactId) {
            return null
          }

          return window.axios.get(`${suffixV1}integrations/hubspot/jit-card/company-association/${contactId}`)
        },

        /**
         * Get HubSpot Inbox Configuration Data
         *
         * @returns Promise<axios.AxiosResponse<{success: boolean, data: object>>
         */
        getInboxSetupData () {
          return window.axios.get(`${suffixV1}integrations/hubspot/inbox`)
        },

        /**
         * Connect HubSpot Inbox to a company
         *
         * @param params
         * @returns {Promise<axios.AxiosResponse<{success: boolean, data: object>>}
         */
        connectInbox (params) {
          return window.axios.post(`${suffixV1}integrations/hubspot/inbox/connect`, params)
        },

        /**
         * Retrieve the conversation threads of a contact
         *
         * @param contactId
         * @returns {Promise<axios.AxiosResponse<{success: boolean, data: object>>}
         */
        getContactConversationThreads (contactId) {
          if (!contactId) {
            return null
          }

          return window.axios.get(`${suffixV1}integrations/hubspot/jit-card/conversation-threads/${contactId}`)
        },

        getContactTickets (contactId) {
          if (!contactId) {
            return null
          }

          return window.axios.get(`${suffixV1}integrations/hubspot/jit-card/contact-tickets/${contactId}`)
        },

        getCompanyDeals (contactId) {
          if (!contactId) {
            return null
          }

          return window.axios.get(`${suffixV1}integrations/hubspot/jit-card/company-deals/${contactId}`)
        },

        /**
         * Get all Aloware contacts associated with a HubSpot deal.
         *
         * @param {string|number} hsDealId
         * @returns {Promise<axios.AxiosResponse<{contacts: Array<{id: number, name: string, phone_number: string}>}>>}
         */
        getDealContacts (hsDealId) {
          return window.axios.get(`${suffixV1}integrations/hubspot/crm-extension/deal-contacts`, {
            params: { hs_deal_id: hsDealId }
          })
        },

        /**
         * Get all Aloware contacts associated with a HubSpot ticket.
         *
         * @param {string|number} hsTicketId
         * @returns {Promise<axios.AxiosResponse<{contacts: Array<{id: number, name: string, phone_number: string}>}>>}
         */
        getTicketContacts (hsTicketId) {
          return window.axios.get(`${suffixV1}integrations/hubspot/crm-extension/ticket-contacts`, {
            params: { hs_ticket_id: hsTicketId }
          })
        },

        /**
         * Resolve the Aloware contact for a HubSpot record reached on a given number.
         *
         * @param {{phoneNumber: string, portalId: string, objectId: string, toPhoneNumberSrc: string, calleeObjectTypeId: string}} params
         * @returns {Promise<axios.AxiosResponse<{contact: {id: number}}>>}
         */
        findContact ({ phoneNumber, portalId, objectId, toPhoneNumberSrc, calleeObjectTypeId }) {
          return window.axios.post(`${suffixV1}integrations/hubspot/find-contact`, {
            params: {
              phoneNumber,
              portalId,
              objectId,
              toPhoneNumberSrc,
              calleeInfo: {
                calleeId: objectId,
                calleeObjectTypeId
              }
            }
          })
        }
      },

      zoho: {
        getViews () {
          return window.axios.get(`${suffixV1}integration/zoho/views`)
        }
      },

      pipedrive: {
        getFilters () {
          return window.axios.get(`${suffixV1}integration/pipedrive/filters`)
        }
      },

      highlevel: {
        getSearchOptions () {
          return window.axios.get(`${suffixV2}contacts-list/highlevel-search-options`)
        }
      }

    },

    flows: {
      enrollable () {
        return window.axios.get(`${suffixV1}flows/enrollable`)
      },

      filterable () {
        return window.axios.get(`${suffixV1}flows/filterable`)
      },

      enroll (flowId, contactId) {
        return window.axios.post(`${suffixV1}flows/${flowId}/enrollments`, { contact_id: contactId })
      },

      exitEnrollment (flowId, enrollmentId) {
        return window.axios.post(`${suffixV1}flows/${flowId}/enrollments/${enrollmentId}/exit`)
      },

      contactEnrollments (contactId, config = {}) {
        return window.axios.get(`${suffixV1}flows/contacts/${contactId}/enrollments`, config)
      },

      bulkEnroll (flowId, payload) {
        return window.axios.post(`${suffixV1}flows/${flowId}/bulk-enrollments`, payload)
      }
    },

    automations: {
      workflows: {
        enroll (workflowId, params) {
          return window.axios.post(`${suffixV1}automations/workflows/${workflowId}/sequence-contacts`, params)
        },

        bulkEnroll (workflowId, params) {
          return window.axios.post(`${suffixV1}automations/workflows/${workflowId}/bulk-tags-sequence-contacts`, params)
        }
      }
    },

    users: {
      withAccessToContact (contactId) {
        return window.axios.get(`${suffixV1}user/get-users-with-access-to-contact?contact_id=${contactId}`)
      },

      setDefaultLogin (userId, params) {
        return window.axios.patch(`${suffixV1}user/${userId}/set-default-app`, params)
      },

      setAgentStatus (userId, params) {
        return window.axios.post(`${suffixV1}user/${userId}/agent-status`,
          typeof params === 'object' ? params : { agent_status: params }
        )
      },

      getCompanyAccesses () {
        return window.axios.get('/api/v1/user/company/accesses')
      }
    },

    reports: {
      communications: {
        get (params) {
          return window.axios.get(`${suffixV1}reports/communications`, params)
        },

        getCount (params) {
          return window.axios.get(`${process.env.API_REPORTING_URL}${suffixV1}reports/communications/count`, params)
        }
      }
    },

    user: {
      getById (id) {
        return window.axios.get(`${suffixV1}user/${id}`)
      },

      update (id, params) {
        return window.axios.put(`${suffixV1}user/${id}`, params)
      },

      getPersonalInbox (id) {
        return window.axios.get(`${suffixV1}user/${id}/personal-inbox`)
      },

      diagnosis (id) {
        return window.axios.get(`${suffixV1}diagnosis/user/${id}`)
      },

      wallboardDefaultFilters: {
        save (params) {
          return window.axios.put(`${suffixV1}user/wallboard-default-filters`, params)
        },

        clear () {
          return window.axios.delete(`${suffixV1}user/wallboard-default-filters`)
        }
      }
    },

    communication: {
      forceTerminate (id) {
        return window.axios.post(`${suffixV1}communication/${id}/force-terminate`)
      },

      async agentForceTerminate (id, params = {}) {
        return window.axios.post(`${suffixV1}agent/communication/${id}/terminate`, params)
      },

      async markAgentHangup (id) {
        return window.axios.post(`${suffixV1}agent/communication/${id}/mark-agent-hangup`)
      },

      forceDequeue (id) {
        return window.axios.post(`${suffixV1}communication/${id}/force-dequeue`)
      },

      get (id, params = {}) {
        return window.axios.get(`${suffixV1}communication/${id}`, { params })
      },

      delete (id) {
        return window.axios.delete(`${suffixV1}communication/${id}`)
      },

      updateNote (id, body) {
        return window.axios.patch(`${suffixV1}communication/${id}/note`, { body })
      },

      reportIssue (id, data) {
        return window.axios.post(`${suffixV1}communications/${id}/report-issue`, data)
      },

      askQuestion (communicationId, params) {
        if (!communicationId) {
          return null
        }

        return window.axios.get(`${suffixV1}communication/${communicationId}/transcription/ask`, params)
      }
    },

    transcription: {
      submitSummaryFeedback (communicationId, feedbackType) {
        return window.axios.post(`${suffixV1}transcription/${communicationId}/summary-feedback`, { feedback: feedbackType })
      },

      fetchSmartTranscriptionData (communicationId, options) {
        return window.axios.get(`${suffixV1}transcription/communication/${communicationId}`, options)
      },

      // Generate transcription for the communication
      generateTranscription (communicationId) {
        return window.axios.post(`${suffixV1}transcription/communication/${communicationId}`)
      },

      // Generate summary for the transcription
      generateSummary (communicationId) {
        return window.axios.post(
          `${suffixV1}transcription/communication/${communicationId}/generate-summary`
        )
      },

      // Update transcription summary
      updateSummary (communicationId, summary) {
        return window.axios.post(
          `${suffixV1}transcription/${communicationId}/update-summary`,
          { summary }
        )
      }
    },

    statics: {
      get (companyId) {
        return window.axios.get('/get-statics', {
          params: {
            company_id: companyId
          }
        })
      }
    },

    library: {
      voicemailDrop: {
        get (params) {
          return window.axios.get(`${suffixV1}voicemail-drop`, params)
        },

        create (params) {
          return window.axios.post(`${suffixV1}voicemail-drop`, params)
        },

        delete (id) {
          return window.axios.delete(`${suffixV1}voicemail-drop/${id}`)
        }
      }
    },

    profile: {
      store (params) {
        return window.axios.post(`${suffixV1}profile`, params)
      },

      getAgentStatus () {
        return window.axios.post(`${suffixV1}profile/get-agent-status`, { device_info: null })
      }
    },

    urlShortener: {
      generate (text) {
        return window.axios.post('/api/v1/url-shortener/urls/parse', { text })
      },

      domains () {
        return window.axios.get(`${suffixV1}url-shortener/domains`)
      }
    },

    broadcasts: {
      delete (id) {
        return window.axios.delete(`${suffixV1}broadcasts/${id}`)
      },
      enable (id) {
        return window.axios.post(`${suffixV1}broadcasts/${id}/toggle-active`)
      },
      get (params = {}) {
        return window.axios.get(`${suffixV1}broadcasts`, { params })
      },
      sendBulkMessage (params) {
        return window.axios.post(`${suffixV1}broadcasts/send-bulk-messages`, params)
      },
      sendBulkRvm (params) {
        return window.axios.post(`${suffixV1}broadcasts/send-bulk-rvm`, params)
      },
      update (id, broadcast) {
        return window.axios.put(`${suffixV1}broadcasts/${id}`, broadcast)
      },
      toggleStatus (id) {
        return window.axios.post(`${suffixV1}broadcasts/${id}/toggle-active`)
      }
    },
    carrierFees: {
      get () {
        return window.axios.get(`${suffixV1}carrier-fees`)
      }
    },
    contactCenter: {
      summary: {
        get (params) {
          return window.axios.post(`${suffixV1}contact-center/summary-report`, params)
        }
      },

      liveCalls: {
        get (params) {
          return window.axios.post(`${suffixV1}contact-center/live-calls`, params)
        }
      },

      parkedCalls: {
        get (params) {
          return window.axios.post(`${suffixV1}contact-center/parked-calls`, params)
        }
      },

      queuedCalls: {
        get (params) {
          return window.axios.post(`${suffixV1}contact-center/queued-calls`, params)
        }
      }
    },

    dialer: {
      sendVmDrop (params) {
        return window.axios.post(`${suffixV1}dialer/play-prerecorded-voicemail`, params)
      }
    },

    company: {
      get (params) {
        return window.axios.get(`${suffixV1}company/${params.id}`)
      },

      /**
       * Endpoint for fetching agents status.
       * params parameter is used to paginate the data that will be fetch
       * @handler Api/V1/CompanyController@getAgentsStatus
       * @return Promise
       */
      getAgentsStatus () {
        return window.axios.get(`${suffixV1}company/agents-status/get`)
      }
    },

    communicationScript: {
      store (params) {
        return window.axios.post(`${suffixV1}communication-script`, params)
      }
    },

    auth: {
      impersonate (userId, userAccess = false) {
        let isImpersonating = true
        localStorage.removeItem('company_id')
        localStorage.removeItem('current_user_id')

        if (userAccess) {
          isImpersonating = false
        }

        return window.axios.post(`${suffixV1}user/${userId}/impersonate`, {
          is_impersonated: isImpersonating
        }).then((res) => {
          localStorage.setItem('api_token', res.data.api_token)
          window.axios.defaults.headers.common['Authorization'] = 'Bearer ' + localStorage.getItem('api_token')

          localStorage.setItem('company_id', res.data.company_id)
          localStorage.setItem('current_user_id', res.data.user_id)

          return res.data
        }).catch((err) => {
          return Promise.reject(err)
        })
      },

      switchCompany (userId) {
        localStorage.removeItem('company_id')
        localStorage.removeItem('current_user_id')

        return window.axios.post(`${suffixV1}user/${userId}/switch-company`).then((res) => {
          localStorage.setItem('api_token', res.data.api_token)
          window.axios.defaults.headers.common['Authorization'] = 'Bearer ' + localStorage.getItem('api_token')

          localStorage.setItem('company_id', res.data.company_id)
          localStorage.setItem('current_user_id', res.data.user_id)

          return res.data
        })
      }
    },

    importWizard: {
      parseUrl: `${suffixV1}import-wizard/parse`,

      analyze (headers, ignoreFirstRow, importId) {
        return window.axios.post(`${suffixV1}import-wizard/analyze`, {
          headers,
          ignore_first_row: ignoreFirstRow,
          import_id: importId
        })
      },

      startImport (importId, data) {
        return window.axios.post(`${suffixV1}import-wizard/${importId}/start`, data)
      }
    },

    consent: {
      getMissedCallHandler () {
        return window.axios.get(`${suffixV1}consent/aloai_missed_call_handler`)
      },
      getMissedCallHandlerExtra () {
        return window.axios.get(`${suffixV1}consent/aloai_missed_call_handler/extra`)
      },
      postMissedCallHandler (accepted) {
        return window.axios.post(`${suffixV1}consent/aloai_missed_call_handler`, { accepted })
      }
    },

    companies: {
      toggleFlag (flagName, enabled) {
        return window.axios.patch(`${suffixV1}company/flag`, {
          flag_name: flagName,
          enabled: enabled
        })
      },
      get (companyId) {
        return window.axios.get(`${suffixV1}company/${companyId}`)
      }
    }
  },

  V2: {
    contact: {
      getConversationSummary (contactId, params) {
        if (!contactId) {
          return null
        }

        return window.axios.get(`/api/v2/contacts/${contactId}/conversation-summary`, params)
      },

      askQuestion (contactId, params) {
        if (!contactId) {
          return null
        }

        return window.axios.get(`/api/v2/contacts/${contactId}/conversation-summary/ask`, params)
      },

      getTextMessageSuggestions (contactId, params) {
        if (!contactId) {
          return window.axios.get(`/api/v2/contacts/text-message-suggestions`, params)
        }

        return window.axios.get(`/api/v2/contacts/${contactId}/text-message-suggestions`, params)
      }
    },

    contacts: {
      get (id, sourceToken = null) {
        if (!id || id === 'undefined') {
          return Promise.reject(new Error('Failed to process contact fetch: Missing contact id!'))
        }

        if (sourceToken) {
          return window.axios.get(`/api/v2/contacts/${id}`, {
            cancelToken: sourceToken
          })
        }

        return window.axios.get(`/api/v2/contacts/${id}`)
      },

      list (params, cancelTokenSource) {
        params.relations = _.get(params, 'relations', [
          'tags'
        ])

        delete params.timezone

        return window.axios.get(`/api/v2/contacts`, { params, paramsSerializer: qs.stringify, cancelToken: cancelTokenSource })
      },

      counts (params, cancelTokenSource) {
        delete params.timezone

        return window.axios.get(`${process.env.API_REPORTING_URL}/api/v2/contacts/count`, { params, paramsSerializer: qs.stringify })
      },

      async listExport (id, params = {}) {
        let defaultList = null
        Object.entries(DEFAULT_PINNED_LIST).forEach(([key, value]) => {
          if (value.id === id) {
            defaultList = DEFAULT_PINNED_LIST[key]
          }
        })

        if (!defaultList) {
          params.contact_list_id = id
        } else {
          params.default_list_type = defaultList.listType
        }

        return window.axios.get(`api/v2/contacts-list/export-csv`, {
          params: params
        })
      },

      removeContactFromList (id, body = {}) {
        return window.axios.post(`${suffixV2}contacts/${id}/remove-from-lists`, body)
      },

      getPropertyActivityLog (contactId, params) {
        if (!contactId) {
          return Promise.reject(new Error('Missing contact id for property activity log'))
        }

        return window.axios.get(`${suffixV2}activity-logs/contacts/${contactId}/property-activity-log`, { params })
      },

      exportCommunications
    },

    contactFolders: {
      list (params) {
        return window.axios.get(`${suffixV2}contact-folders`, { params })
      },

      delete (id) {
        return window.axios.delete(`${suffixV2}contact-folders/${id}`)
      }
    },

    powerDialerFolders: {
      list (params = {}) {
        return window.axios.get(`${suffixV2}power-dialer-folders`, {
          params: params
        })
      },

      delete (id) {
        return window.axios.delete(`${suffixV2}power-dialer-folders/${id}`)
      }
    },

    powerDialerListItem: {
      add (params = {}) {
        return window.axios.post(`${suffixV2}power-dialer-list-items`, params)
      }
    },

    powerDialerAgentSessions: {
      start (contactListId) {
        return window.axios.post(`${suffixV2}power-dialer-agent-sessions`, { contact_list_id: contactListId })
      },

      recordEvent (sessionId, event) {
        return window.axios.post(`${suffixV2}power-dialer-agent-sessions/${sessionId}/${event}`)
      },

      end (sessionId, reason) {
        return window.axios.post(`${suffixV2}power-dialer-agent-sessions/${sessionId}/end`, { reason })
      }
    },

    powerDialer: {
      async listExport (id, params = {}) {
        params.contact_list_id = id
        return window.axios.get(`api/v2/power-dialer-lists/export-csv`, {
          params: params
        })
      },

      userMyQueue (params = {}) {
        return window.axios.get(`api/v2/power-dialer-lists/my-queue`, {
          params: params
        })
      }
    },

    powerDialerAgentQueues: {
      list () {
        return window.axios.get(`${suffixV2}power-dialer-agent-queues`)
      },

      clear (userIds) {
        return window.axios.post(`${suffixV2}power-dialer-agent-queues/clear`, {
          user_ids: userIds
        })
      }
    },

    contactsList: {
      assignContactsTo (contactListId, payload = {}) {
        const params = {
          assign_to: payload.assign_contacts_to,
          id: payload.assign_contacts_to === 'ring_group' ? payload.ring_group_id : payload.user_id,
          force: payload.force,
          from_talk: true
        }
        return window.axios.post(`${suffixV2}contacts-list/${contactListId}/assign`, params)
      },

      splitListIntoSmallerLists (contactListId, params) {
        return window.axios.post(`${suffixV2}contacts-list/${contactListId}/split`, params)
      },

      itemsCount (contactListId) {
        const reportingUrl = process.env.API_REPORTING_URL || ''

        return window.axios.get(`${reportingUrl}${suffixV2}contacts-list/${contactListId}/count`)
      },

      changeOwner (contactListId, userId) {
        const params = {
          user_id: userId
        }
        return window.axios.patch(`${suffixV2}contacts-list/${contactListId}/change-owner`, params)
      }
    },

    contactListItem: {
      addContact (contactListId, contacts = []) {
        return window.axios.post(`${suffixV2}contact-list-items`, {
          contact_list_id: contactListId,
          contacts: contacts
        })
      }
    },

    integrations: {
      salesforce: {
        importList (target, params) {
          return window.axios.post(`${suffixV2}contacts-list/import-salesforce-list/${target}`, params)
        },
        listExists (target) {
          return window.axios.get(`${suffixV2}contacts-list/salesforce-list-exists/${target}`)
        }
      },
      hubspot: {
        importList (target, params) {
          return window.axios.post(`${suffixV2}contacts-list/import-hubspot-list/${target}`, params)
        },
        listExists (target) {
          return window.axios.get(`${suffixV2}contacts-list/hubspot-list-exists/${target}`)
        },
        importListToPowerDialer (target, params) {
          return window.axios.post(`${suffixV2}power-dialer-lists/import-hubspot-list/${target}`, params)
        }
      },

      zoho: {
        importView (target, params) {
          return window.axios.post(`${suffixV2}contacts-list/import-zoho-view/${target}`, params)
        },
        viewExists (target) {
          return window.axios.get(`${suffixV2}contacts-list/zoho-view-exists/${target}`)
        },
        importViewToPowerDialer (target, params) {
          return window.axios.post(`${suffixV2}power-dialer-lists/import-zoho-view/${target}`, params)
        }
      },

      pipedrive: {
        importFilter (target, params) {
          return window.axios.post(`${suffixV2}contacts-list/import-pipedrive-filter/${target}`, params)
        },
        filterExists (target) {
          return window.axios.get(`${suffixV2}contacts-list/pipedrive-filter-exists/${target}`)
        },
        importFilterToPowerDialer (target, params) {
          return window.axios.post(`${suffixV2}power-dialer-lists/import-pipedrive-filter/${target}`, params)
        }
      },

      highlevel: {
        importCriteria (params) {
          return window.axios.post(`${suffixV2}contacts-list/import-highlevel-criteria`, params)
        },
        getSearchOptions () {
          return window.axios.get(`${suffixV2}contacts-list/highlevel-search-options`)
        },
        importCriteriaToPowerDialer (params) {
          return window.axios.post(`${suffixV2}power-dialer-lists/import-highlevel-criteria`, params)
        }
      }

    },

    contactList: {
      get (params) {
        return window.axios.get(`${suffixV2}contacts-list`, { params })
      },
      public (params) {
        return window.axios.get(`${suffixV2}contacts-list/public`, { params })
      },
      async update (id, params) {
        return window.axios.put(`${suffixV2}contacts-list/${id}`, params)
      },
      delete (id, params) {
        return window.axios.delete(`${suffixV2}contacts-list/${id}`, { params })
      },
      blockPreview (id) {
        return window.axios.get(`${suffixV2}contacts-list/${id}/block-preview`)
      },
      bulkBlock (id) {
        return window.axios.post(`${suffixV2}contacts-list/${id}/bulk-block`)
      },
      bulkUnblock (id) {
        return window.axios.post(`${suffixV2}contacts-list/${id}/bulk-unblock`)
      },
      bulkBlockAction (bulkActionId) {
        return window.axios.get(`${suffixV2}contacts-list/bulk-block-actions/${bulkActionId}`)
      }
    },

    filters: {
      get (tags = false) {
        if (!tags) {
          // Request for filters without tags as options
          const params = { exclude_tags: true }
          return window.axios.get(`${suffixV2}contacts/filters`, { params })
        }
        return window.axios.get(`${suffixV2}contacts/filters`)
      }
    },

    mentions: {
      get (params) {
        return window.axios.get(`${suffixV2}mentions`, params)
      }
    },

    reservations: {
      reject (reservationSid) {
        return window.axios.post(`${suffixV2}reservations/reject`, { reservation_sid: reservationSid })
      }
    },

    inbox: {
      filters: {
        get (params) {
          return window.axios.get(`${suffixV2}filters`, { params: params })
        },

        save (params) {
          return window.axios.post(`${suffixV2}filters`, params)
        },

        update (filterId, params) {
          return window.axios.put(`${suffixV2}filters/${filterId}`, params)
        },

        delete (filterId) {
          return window.axios.delete(`${suffixV2}filters/${filterId}`)
        }
      }
    },

    users: {
      get (params) {
        return window.axios.get(`${suffixV2}users`, { params })
      }
    },

    aloAiBot: {
      getBots (params = {}) {
        // Convert params to query string
        if (params.direction) {
          params.direction = params.direction === AloAi.DIRECTION_OUTBOUND ? 'outbound' : 'inbound'
        }

        if (params.type) {
          params.type = params.type === AloAi.TYPE_TEXT ? 'text' : 'voice'
        }
        return window.axios.get(`${suffixV1}aloai/bot`, { params })
      },
      getContactDisengagedBots (contactId) {
        return window.axios.get(`${suffixV1}aloai/contacts/${contactId}/disengaged-bots`)
      },
      getContactBotEnrollments (contactId) {
        return window.axios.get(`${suffixV1}aloai/contacts/${contactId}/enrollments`)
      },
      updateContactEngagements (contactId, engagements) {
        return window.axios.post(`${suffixV1}aloai/contacts/${contactId}/engagement-status`, { engagements })
      },
      enrollContacts (botId, params) {
        return window.axios.post(`${suffixV1}aloai/bot/${botId}/enroll-contacts`, params)
      },
      disenrollContact (botId, params) {
        return window.axios.post(`${suffixV1}aloai/bot/${botId}/disenroll-contact`, params)
      }
    },

    companies: {
      toggleFeature (companyId, feature) {
        return window.axios.put(`${suffixV2}companies/${companyId}/features/${feature}`)
      },
      toggleFlag (flagName, enabled) {
        return window.axios.patch(`${suffixV1}company/flag`, {
          flag_name: flagName,
          enabled: enabled
        })
      },
      get (companyId) {
        return window.axios.get(`${suffixV1}company/${companyId}`)
      }
    },

    callFeedback: {
      storeFeedback (params) {
        return window.axios.post(`${suffixV2}call-feedbacks`, params)
      }
    },

    calendarConnections: {
      getDrivers () {
        return window.axios.get(`${suffixV2}calendar/connections/drivers`)
      },

      getDriverSettings () {
        return window.axios.get(`${suffixV2}calendar/drivers/settings`)
      },

      get () {
        return window.axios.get(`${suffixV2}calendar/connections`)
      },

      getAuthorizationUrl (driver, visibility, returnPath) {
        return window.axios.get(`${suffixV2}calendar/connections/oauth/${driver}/authorize`, {
          params: { visibility, return_path: returnPath }
        })
      },

      getTargets (connectionId) {
        return window.axios.get(`${suffixV2}calendar/connections/${connectionId}/targets`)
      },

      getAvailability (connectionId, params) {
        return window.axios.get(`${suffixV2}calendar/connections/${connectionId}/availability`, { params })
      },

      createBooking (connectionId, contactId, payload) {
        return window.axios.post(`${suffixV2}calendar/connections/${connectionId}/bookings/contact/${contactId}`, payload)
      },

      listContactBookings (contactId) {
        return window.axios.get(`${suffixV2}calendar/bookings/contact/${contactId}`)
      },

      cancelBooking (connectionId, bookingId, contactId) {
        return window.axios.delete(`${suffixV2}calendar/connections/${connectionId}/bookings/${bookingId}/contact/${contactId}`)
      },

      rescheduleBooking (connectionId, bookingId, contactId, payload) {
        return window.axios.patch(`${suffixV2}calendar/connections/${connectionId}/bookings/${bookingId}/contact/${contactId}`, payload)
      },

      update (connectionId, params) {
        return window.axios.patch(`${suffixV2}calendar/connections/${connectionId}`, params)
      },

      disconnect (connectionId) {
        return window.axios.delete(`${suffixV2}calendar/connections/${connectionId}`)
      },

      getRecommendationNotice () {
        return window.axios.get(`${suffixV2}calendar/recommendation-notice`)
      }
    }
  }
}

export default talk2Api
